<template>
  <div class="container is-fluid py-5">

    <v-form 
      v-if="!firstLoad"
      :valid.sync="valid">

      <div class="level" v-if="!delivery.is_delivered">

        <div class="level-left flex flex--align-start">
          <div class="mx-3">
            <label class="label">Expected Delivery Date*</label>
            <v-datepicker 
              required
              :disabled="!editable"
              placeholder="Expected delivery date"
              :value="parse(delivery.exp_delivery_date)"
              @input="update('exp_delivery_date', $event)"
              size="is-small">
            </v-datepicker>
          </div>
          
          <div class="mx-3" v-if="delivery.ready_for_shipping_on">
            <label class="label">Ready for Shipping On</label>
            <div class="mt-2">
              {{ format(parse(delivery.ready_for_shipping_on)) }}
            </div>
          </div>
          
          <div class="mx-3" v-if="delivery.shipped_on">
            <label class="label">Shipped On</label>
            <div class="mt-2">
              {{ format(parse(delivery.shipped_on)) }}
            </div>
          </div>

          <div class="mx-3" v-if="delivery.delivered_on">
            <label class="label">Delivered On</label>
            <div class="mt-2">
              {{ format(parse(delivery.delivered_on)) }}
            </div>
          </div>
        </div>

        <div class="level-right flex flex--align-start">
          <v-datepicker-modal-slot
            has-time
            v-if="!delivery.ready_for_shipping_on"
            :value="delivery.ready_for_shipping_on"
            @input="update(delivery.id, 'ready_for_shipping_on', $event.toISOString())">
            <button 
              @click.prevent="" 
              class="is-link is-fullwidth button">
              Mark as Ready for Shipping
            </button>
          </v-datepicker-modal-slot>

          <button 
            v-if="delivery.is_ready_for_shipping && !delivery.shipped_on"
            @click.prevent="addingShippingInfo = true" 
            class="is-link is-fullwidth button">
            Mark as Shipped
          </button>

          <v-datepicker-modal-slot
            has-time
            v-if="delivery.is_shipped && !delivery.delivered_on"
            :value="delivery.delivered_on"
            @input="update(delivery.id, 'delivered_on', $event.toISOString())">
            <button 
              @click.prevent="" 
              class="is-link is-fullwidth button">
              Mark as Delivered
            </button>
          </v-datepicker-modal-slot>
        </div>

      </div>

      <hr/>

      <div class="columns is-multiline">

        <div class="column is-3">
          <label class="label">Project*</label>
          <div class="mt-2">
            {{ delivery.project.project_name }}
          </div>
        </div>

        <div class="column is-3">
          <label class="label">Delivery Number*</label>
          <div class="mt-2">
            {{ delivery.delivery_no }}
          </div>
        </div>

        <div class="column is-half">
          <label class="label">Description</label>
          <v-text-field 
            is-small
            :disabled="!editable"
            placeholder="Enter delivery description"
            v-model="delivery.delivery_desc"
            @blur="update('delivery_desc', $event)">
          </v-text-field>
        </div>
        
        <div class="column is-2">
          <label class="label">Delivery Contact Email*</label>
          <v-text-field
            required 
            is-small
            :disabled="!editable"
            placeholder="Enter delivery contact email"
            v-model="delivery.email"
            @blur="update('email', $event)"
            :rules="emailRules">
          </v-text-field>
        </div>

        <div class="column is-2">
          <label class="label">Delivery Phone Number*</label>
          <v-text-field
            required 
            is-small
            :disabled="!editable"
            placeholder="Enter delivery phone number"
            v-model="delivery.phone1"
            @blur="update('phone1', $event)">
          </v-text-field>
        </div>

        <div class="column is-2">
          <label class="label">Site Manager Name*</label>
          <v-text-field
            required 
            is-small
            :disabled="!editable"
            placeholder="Enter site manager name"
            v-model="delivery.site_manager_name"
            @blur="update('site_manager_name', $event)">
          </v-text-field>
        </div>

        <div class="column is-2">
          <label class="label">Site Manager Email*</label>
          <v-text-field
            required 
            is-small
            :disabled="!editable"
            placeholder="Enter site manager email"
            v-model="delivery.site_manager_email"
            @blur="update('site_manager_email', $event)">
          </v-text-field>
        </div>

        <div class="column is-2">
          <label class="label">Project Manager Name*</label>
          <v-text-field
            required 
            is-small
            :disabled="!editable"
            placeholder="Enter project manager name"
            v-model="delivery.project_manager_name"
            @blur="update('project_manager_name', $event)">
          </v-text-field>
        </div>

        <div class="column is-2">
          <label class="label">Project Manager Email*</label>
          <v-text-field
            required 
            is-small
            :disabled="!editable"
            placeholder="Enter project manager email"
            v-model="delivery.project_manager_email"
            @blur="update('project_manager_email', $event)">
          </v-text-field>
        </div>

        <div class="column is-half">
          <label class="label">Delivery Address*</label>
          <v-text-field 
            required
            is-small
            :disabled="!editable"
            placeholder="Enter delivery address"
            v-model="delivery.address"
            @blur="update('address', $event)">
          </v-text-field>
        </div>

        <div class="column is-2">
          <label class="label">Delivery City*</label>
          <v-text-field 
            required
            is-small
            :disabled="!editable"
            placeholder="Enter delivery city"
            v-model="delivery.city"
            @blur="update('city', $event)">
          </v-text-field>
        </div>

        <div class="column is-2">
          <label class="label">Delivery Province/ State*</label>
          <v-text-field 
            required
            is-small
            :disabled="!editable"
            placeholder="Enter delivery province/ state"
            v-model="delivery.province_state"
            @blur="update('province_state', $event)">
          </v-text-field>
        </div>

        <div class="column is-2">
          <label class="label">Delivery Postal Code*</label>
          <v-text-field 
            required
            is-small
            :disabled="!editable"
            placeholder="Enter delivery postal code"
            v-model="delivery.postal_code"
            @blur="update('postal_code', $event)">
          </v-text-field>
        </div>

        <div class="column is-4">
          <label class="label">Truck Driver Name*</label>
          <v-text-field 
            required
            is-small
            :disabled="!editable"
            placeholder="Enter truck driver's name"
            v-model="delivery.truck_driver_name"
            @blur="update('truck_driver_name', $event)">
          </v-text-field>
        </div>

        <div class="column is-4">
          <label class="label">Truck Driver Phone*</label>
          <v-text-field 
            required
            is-small
            :disabled="!editable"
            placeholder="Enter truck driver's phone"
            v-model="delivery.truck_driver_phone"
            @blur="update('truck_driver_phone', $event)">
          </v-text-field>
        </div>

        <div class="column is-4">
          <label class="label">Truck License Plate*</label>
          <v-text-field 
            required
            is-small
            :disabled="!editable"
            placeholder="Enter truck number or license plate"
            v-model="delivery.truck_driver_lp"
            @blur="update('truck_driver_lp', $event)">
          </v-text-field>
        </div>
      </div>

    </v-form>

    <hr/>

    <div>

      <div 
        class="level" 
        v-if="!firstLoad && editable">
        <button
          @click="addingItem = true" 
          class="button is-primary">
          Add an Item to this Delivery
        </button>
        <button 
          class="button"
          v-if="selected.length > 0"
          @click="launchConfirmDelete()">
          <span class="icon is-medium">
            <i class="fas fa-trash has-text-danger"></i>
          </span>
          <span>Delete {{ selected.length }} selected delivery items</span>
        </button>
      </div>

      <header class="menu-label">
        Delivery Items
      </header>

      <article 
        v-if="!firstLoad && delivery.delivery_storages.length == 0" 
        class="message is-info is-fullwidth">
        <div class="message-body">
          There are no items in this delivery.
        </div>
      </article>

      <table 
        class="table is-fullwidth has-small-font sticky-header"
        v-if="!firstLoad && delivery.delivery_storages.length > 0">

        <thead>
          <tr>
            <th>
              <v-checkbox
                :value="allSelected"
                @input="toggleAllSelected($event)"/>
            </th>
            <th 
              v-for="(header, headerIndex) in headers"
              :key="headerIndex">
              {{ header }}
            </th>
          </tr>
        </thead>

        <tbody>
          <template v-for="storage in delivery.delivery_storages">
            <tr
              class="subheader" 
              :key="storage.id">
              <td>
                <v-checkbox
                  :value="selected.includes(storage.id)"
                  @input="toggleSelected(storage.id)"/>
              </td>
              <td>
                {{ storage.storage.storage_code }}
              </td>
              <td>
                {{ storage.storage.storage_name }}
              </td>
              <td>
                {{ storage.storage.storage_type.storage_type_name }}
              </td>
              <td>
                ({{ storage.storage.warehouse_location.warehouse.warehouse_code }})
                {{ storage.storage.warehouse_location.warehouse.warehouse_name }}
              </td>
              <td>
                ({{ storage.storage.warehouse_location.location_code }})
                {{ storage.storage.warehouse_location.location_name }}
              </td>
            </tr>

            <tr 
              class="has-tiny-font has-background-light"
              v-if="storage.storage.storage_units.length > 0"
              :key="`${storage.id}-storage-units-header`">
              <th 
                v-for="(header, headerIndex) in storageUnitHeaders"
                :key="headerIndex">
                {{ header }}
              </th>
            </tr>

            <tr 
              class="has-background-light"
              v-for="storageUnit in storage.storage.storage_units"
              :key="`${storageUnit.id}-storage-unit`">
              <td></td>
              <td>
                {{ storageUnit.stocking_unit.product_master.product_category.category }}
              </td>
              <td>
                <span v-if="storageUnit.stocking_unit.product_master.product_subcategory">
                  {{ storageUnit.stocking_unit.product_master.product_subcategory.subcategory }}
                </span>
                <span v-else class="has-text-grey">
                  No subcategory
                </span>
              </td> 
              <td>
                <div>
                  {{ storageUnit.stocking_unit.product_master.name }}
                </div>
                <div class="help">
                  <span v-if="storageUnit.stocking_unit.product_master.product_category.has_dimensions">
                    {{ lwt(storageUnit.stocking_unit) }}
                  </span>
                  <span>
                    {{ storageUnit.stocking_unit.product_master.size }}
                  </span>
                </div>
              </td>
              <td>
                {{ storageUnit.stocking_unit.product_master.unit ? 
                  storageUnit.stocking_unit.product_master.unit.unit_display_name : '' }}
              </td>
              <td>
                {{ storageUnit.qty }}
              </td>
            </tr>

          </template>
        </tbody>
      </table>
      
      <template v-if="!firstLoad && delivery.images && delivery.images.length > 0">
        <hr/>
        <header class="menu-label">
          Shipping Images
        </header>
        <img 
          v-for="(image, i) in delivery.images"
          :key="i"
          :src="image"/>
      </template>
      
    </div>

    <add-shipping-information-modal
      v-model="addingShippingInfo"
      @save="addShippingInfo"/>

    <add-delivery-item-modal
      v-if="!firstLoad"
      v-model="addingItem"
      :project-id="delivery.project.id"
      :delivery-id="delivery.id"
      :exclude="delivery.delivery_storages.map(d => d.storage_id)"/>

    <v-confirm-modal 
      v-model="confirmDelete.state"
      :title="confirmDelete.title"
      :message="confirmDelete.message"
      :type="confirmDelete.type"
      @confirm="deleteConfirmedCb()"
      @cancel="confirmDelete.state = false"/>

  </div>
</template>

<script>
import VCheckbox from '@/components/base/VCheckbox'
import VForm from '@/components/base/VForm'
import VTextField from '@/components/base/VTextField'
import VDatepicker from '@/components/base/VDatepicker'
import VDatepickerModalSlot from '@/components/base/VDatepickerModalSlot'
import VConfirmModal from '@/components/base/VConfirmModal'

import AddShippingInformationModal from '@/components/delivery/AddShippingInformationModal'
import AddDeliveryItemModal from '@/components/delivery/AddDeliveryItemModal'

import { mapState, mapActions, mapMutations } from 'vuex'

import { parseISO, format } from 'date-fns'

import helpers from '@/helpers'

export default {
  components: { 
    VCheckbox,
    VForm, 
    VTextField, 
    VDatepicker, 
    VDatepickerModalSlot, 
    VConfirmModal,
    AddShippingInformationModal,
    AddDeliveryItemModal
  },
  data() {
    return {
      valid: false,
      headers: [
        'Storage Code', 'Storage Name', 'Storage Type', 'Warehouse',
        'Warehouse Space'
      ],
      storageUnitHeaders: [
        '', 'Product Category', 'Product Subcategory', 'Product', 
        'Product Unit', 'Product Quantity'
      ],
      parse: (d) => {
        return d instanceof Date ? d : parseISO(d);
      },
      format: (d) => {
        return d ? format(d, 'yyyy-MM-dd H:mm:ss') : null
      },
      addingShippingInfo: false,
      emailRules: [helpers.validateEmail],
      addingItem: false,
      confirmDelete: {
        state: false,
        title: 'Delete Confirmation',
        message: 'Are you sure you want to delete this item from this delivery?',
        type: 'danger'
      },
      deleteConfirmedCb: null,
    }
  },
  computed: {
    ...mapState("delivery", [
      "delivery", "loading", "firstLoad", "selected", "allSelected"
    ]),
    editable() {
      return this.delivery.is_shipped == false;
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("delivery", [
      "getDelivery", "updateDelivery", "updateItem", 
      "deleteItem", "addImages", "toggleAllSelected"
    ]),
    ...mapMutations("delivery", [
      "setDelivery", "setLoading", "setFirstLoad", "toggleSelected"
    ]),
    initialize() {
      if (this.$route.params.delivery) {
        this.setDelivery(this.$route.params.delivery);
        this.setLoading(false);
        this.setFirstLoad(false);
      } else {
        this.getDelivery(this.$route.params.deliveryId);
      }
    },
    lwt(product) {
      if (product) {
        return `L${product.length_in_str}" x W${product.width_in_str}" x T${product.thick_in_str}"`
      }
    },
    update(field, value) {
      let data = {}
      data[field] = value;

      this.updateDelivery({
        deliveryId: this.delivery.id,
        data: data
      })
    },
    launchConfirmDelete(id) {
      if (!id) {
        this.deleteConfirmedCb = () => {
          this.deleteSelected();
        };
        this.confirmDelete.message = `Are you sure you want to delete the ${this.selected.length} selected items from this delivery?`;
      } else {
        this.deleteConfirmedCb = () => {
          this.deleteItem(id).then(() => {
            this.confirmDelete.state = false;
          })
        };
        this.confirmDelete.message = 'Are you sure you want to delete this item from this delivery?'
      }

      this.confirmDelete.state = true;
    },
    deleteSelected() {
      this.selected.forEach(id => {
        this.deleteItem(id);
      })
      this.confirmDelete.state = false;
    },
    addShippingInfo(data) {
      this.updateDelivery({
        deliveryId: this.editingId,
        data: data.data
      });

      let formData = new FormData;
      data.files.forEach(file => {
        formData.append('images', file.file, file.file.name);
      });

      this.addImages({
        deliveryId: this.editingId,
        data: formData
      });
    },
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';
@import '@/styles/flexbox.scss';
</style>